import { useState, useEffect, useCallback, useRef } from 'react';
import { Memory, apiService } from '../services/api';

interface UseMemorySearchQueryOptions {
  spaceId?: string;
  enabled?: boolean;
}

interface UseMemorySearchQueryResult {
  memories: Memory[];
  isLoading: boolean;
  error: Error | null;
  search: (query: string) => Promise<void>;
  clearSearch: () => void;
  isSearching: boolean;
}

/**
 * Hook for searching memories (semantic search; separate from paginated browse).
 */
export function useMemorySearchQuery(options: UseMemorySearchQueryOptions = {}): UseMemorySearchQueryResult {
  const { spaceId, enabled = true } = options;
  const [memories, setMemories] = useState<Memory[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [, setCurrentQuery] = useState<string>('');
  const mountedRef = useRef(true);
  const abortControllerRef = useRef<AbortController | null>(null);

  const search = useCallback(async (query: string) => {
    if (!enabled) return;

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    if (!query.trim()) {
      setMemories([]);
      setIsSearching(false);
      setCurrentQuery('');
      return;
    }

    try {
      setIsLoading(true);
      setIsSearching(true);
      setError(null);
      setCurrentQuery(query);

      const searchResults = spaceId
        ? await apiService.searchMemoriesInSpace(
            { query: query.trim(), limit: 50 },
            spaceId
          )
        : await apiService.searchMemories({ query: query.trim(), limit: 50 });

      if (abortControllerRef.current.signal.aborted || !mountedRef.current) {
        return;
      }

      setMemories(searchResults);
    } catch (err: any) {
      if (abortControllerRef.current.signal.aborted || !mountedRef.current) {
        return;
      }

      if (err.name === 'AbortError' || err.name === 'CanceledError') {
        return;
      }

      console.error('Failed to search memories:', err);
      setError(err instanceof Error ? err : new Error('Failed to search memories'));
      setMemories([]);
    } finally {
      if (mountedRef.current && !abortControllerRef.current.signal.aborted) {
        setIsLoading(false);
      }
    }
  }, [spaceId, enabled]);

  const clearSearch = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    setMemories([]);
    setIsSearching(false);
    setCurrentQuery('');
    setError(null);
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return {
    memories,
    isLoading,
    error,
    search,
    clearSearch,
    isSearching,
  };
}
